import { useStoreState } from "pullstate";
import React, { useEffect, useMemo, useState } from "react";
import { CurrentScan } from "../../stores/CurrentScan";
import {
  cleanTextContent,
  countWords,
  getCategoryForWord,
  getCategoryNamesAndColors,
  getContrastColor,
} from "./helpers";
import UploadDoc from "./UploadDoc";
import {
  Button,
  Box,
  Typography,
  Container,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
const Transitions = () => {
  const [htmlContent, setHtmlContent] = useState("");
  const [highlightedContent, setHighlightedContent] = useState("");
  const [matchingWordCount, setMatchingWordCount] = useState(0);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const [isContentEditable, setIsContentEditable] = useState(true);
  const { content, loading } = useStoreState(CurrentScan);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const maxWords = 10000;

  const transitionWords = new Map([
    [
      "Causation",
      {
        color: "#FF5733",
        words: [
          "accordingly",
          "as a result",
          "and so",
          "because",
          "consequently",
          "for that reason",
          "hence",
          "on account of",
          "since",
          "therefore",
          "thus",
        ],
      },
    ],
    [
      "Chronology",
      {
        color: "#338FFF",
        words: [
          "after",
          "afterwards",
          "always",
          "at length",
          "during",
          "earlier",
          "following",
          "immediately",
          "in the meantime",
          "later",
          "never",
          "next",
          "now",
          "once",
          "simultaneously",
          "so far",
          "sometimes",
          "soon",
          "subsequently",
          "then",
          "this time",
          "until now",
          "when",
          "whenever",
          "while",
        ],
      },
    ],
    [
      "Lists",
      {
        color: "#33FF57",
        words: [
          "additionally",
          "again",
          "also",
          "and, or, not",
          "as a result",
          "besides",
          "even more",
          "finally",
          "first, firstly",
          "further",
          "furthermore",
          "in addition",
          "in the first place",
          "in the second place",
          "last, lastly",
          "moreover",
          "next",
          "second, secondly, etc.",
          "too",
        ],
      },
    ],
    [
      "Contrast",
      {
        color: "#9933FF",
        words: [
          "after all",
          "although",
          "and yet",
          "at the same time",
          "but",
          "despite",
          "however",
          "in contrast",
          "nevertheless",
          "nonetheless",
          "notwithstanding",
          "on the contrary",
          "on the other hand",
          "otherwise",
          "though",
          "yet",
        ],
      },
    ],
    [
      "Example",
      {
        color: "#FFA933",
        words: [
          "as an illustration",
          "e.g.,", // not working??
          "for example",
          "for instance",
          "specifically",
          "that is",
          "to demonstrate",
          "to illustrate",
        ],
      },
    ],
    [
      "Importance",
      {
        color: "#33FFA9",
        words: [
          "chiefly",
          "critically",
          "foundationally",
          "most importantly",
          "of less importance",
          "primarily",
        ],
      },
    ],
    [
      "Location",
      {
        color: "#996633", ///
        words: [
          "above",
          "adjacent to",
          "below",
          "beyond",
          "centrally",
          "here",
          "nearby",
          "neighboring on",
          "opposite to",
          "peripherally",
          "there",
          "wherever",
        ],
      },
    ],
    [
      "Similarity",
      {
        color: "#6633FF",
        words: [
          "by the same token",
          "in like manner",
          "in similar fashion",
          "in the same way",
          "likewise",
        ],
      },
    ],
    [
      "Clarification",
      {
        color: "#FF33A9",
        words: [
          "i.e.,", // not working ??
          "in other words",
          "that is",
          "that is to say",
          "to clarify",
          "to explain",
          "to put it another way",
          "to rephrase it",
        ],
      },
    ],
    [
      "Concession",
      {
        color: "#999999",
        words: [
          "granted",
          "it is true",
          "naturally",
          "of course",
          "to be sure",
        ],
      },
    ],
    [
      "Conclusion",
      {
        color: "#330000",
        words: [
          "finally",
          "lastly",
          "in conclusion",
          "in the end",
          "to conclude",
        ],
      },
    ],
    [
      "Intensification",
      {
        color: "#33FFFF",
        words: [
          "in fact",
          "indeed",
          "no",
          "of course",
          "surely",
          "to repeat",
          "undoubtedly",
          "without doubt",
          "yes",
        ],
      },
    ],
    [
      "Purpose",
      {
        color: "#A9FF33",
        words: [
          "for this purpose",
          "in order that",
          "so that",
          "to that end",
          "to this end",
        ],
      },
    ],
    [
      "Summary",
      {
        color: "#333333",
        words: [
          "in brief",
          "in sum",
          "in summary",
          "in short",
          "to sum up",
          "to summarize",
        ],
      },
    ],
  ]);

  const clearContent = () => {
    setIsContentEditable(true);
    setHtmlContent("");
    setHighlightedContent("");
    setMatchingWordCount(0);
    setTotalWordCount(0);
  };

  // Memoize the regex pattern
  const regex = useMemo(() => {
    const wordPattern = [...transitionWords.values()]
      .map((categoryInfo) => `\\b(${categoryInfo.words.join("|")})\\b`)
      .join("|");
    return new RegExp(`(${wordPattern})`, "gi");
  }, []);

  const handleSubmit = (content) => {
    let updatedHTML = content;
    const matchingCount = (updatedHTML.match(regex) || []).length;
    updatedHTML = updatedHTML.replace(regex, (match, word) => {
      const lowercaseWord = word.toLowerCase();
      const category = getCategoryForWord(lowercaseWord, transitionWords);
      const bgcolor = category
        ? transitionWords?.get(category)?.color
        : "#ffffff";
      const textColor = getContrastColor(bgcolor);

      return `<span  style="background-color: ${bgcolor}; color: ${textColor};"  contentEditable="false">${match}</span> `;
    });
    setHighlightedContent(updatedHTML);
    setMatchingWordCount(matchingCount);
    setIsContentEditable(false);
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    const htmlContent = e.target.innerHTML;
    const cleanedTextContent = cleanTextContent(htmlContent);
    const totalWords = countWords(cleanedTextContent);
    if (totalWords <= maxWords) {
      setHtmlContent(htmlContent);
      setTotalWordCount(totalWords);
    } else {
      alert(`Maximum word limit exceeded (Limit: ${maxWords} words)`);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, pastedText);
  };

  useEffect(() => {
    if (content) {
      // Set the content coming from scans in the HTML editor when content changes
      setHtmlContent(content);
      const editorDiv = document.getElementById("text-editor"); // Replace with the actual ID of your content-editable div
      if (editorDiv) {
        editorDiv.innerHTML = content; // Set the innerHTML of the div
      }

      const totalWords = countWords(content);
      setTotalWordCount(totalWords);

      // Run the handleSubmit logic here
      const cleanedContent = cleanTextContent(content);
      handleSubmit(cleanedContent);
    }
  }, [content, loading]);

  const categories = getCategoryNamesAndColors(transitionWords);

  return (
    <Container sx={{ width: "100%", py: "20px" }}>
      {totalWordCount < 1 && <UploadDoc CurrentScan={CurrentScan} />}

      <form
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(htmlContent);
        }}>
        {htmlContent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              boxSizing: "border-box",
              justifyContent: "space-between",
              gap: "1rem",
              flexDirection: isMobile ? "column" : "row",
            }}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              width="100%"
              mb="24px">
              <Grid item>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexDirection: isMobile ? "column-reverse" : "row",
                  }}>
                  {totalWordCount > 0 && (
                    <Button
                      onClick={clearContent}
                      variant="contained"
                      sx={{
                        color: "black",
                        backgroundColor: "rgb(48, 188, 237)",
                        boxShadow:
                          "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                        "&:hover": {
                          boxShadow:
                            "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
                        },
                      }}
                      startIcon={<ReplayIcon />}>
                      Clear
                    </Button>
                  )}
                  {matchingWordCount > 0 && totalWordCount > 0 && (
                    <Box
                      sx={{
                        border: "2px solid rgba(31, 38, 65, 0.7)",
                        borderRadius: "16px",
                        padding: "6px 12px",
                      }}>
                      <Typography variant="body3">
                        Matching Words: {matchingWordCount}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item>
                {totalWordCount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      flexDirection: isMobile ? "column-reverse" : "row",
                    }}>
                    <Box
                      sx={{
                        border: "2px solid rgba(31, 38, 65, 0.7)",
                        borderRadius: "16px",
                        padding: "6px 12px",
                      }}>
                      <Typography variant="body3">
                        Words: {totalWordCount}
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<GppGoodOutlinedIcon />}
                      type="submit"
                      disabled={!isContentEditable}
                      variant="contained"
                      sx={{
                        color: "rgba(0, 0, 0, 0.87)",
                        backgroundColor: "rgb(255, 193, 7)",
                        boxShadow:
                          "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                        "&:active, &:focus, &:hover": {
                          backgroundColor: "rgb(178, 135, 4)",
                          boxShadow:
                            "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
                        },
                      }}>
                      Check now
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <div
          contentEditable={isContentEditable} // Enable or disable contentEditable based on isContentEditable
          id="text-editor"
          onInput={handleInputChange}
          onPaste={handlePaste}
          dangerouslySetInnerHTML={{ __html: highlightedContent }}
          placeholder="Start typing or paste your text here..."
        />
      </form>

      <Grid container spacing={1} flexWrap="wrap" gap={3} mt={4}>
        {categories.map((item) => (
          <div
            item
            key={item.name}
            style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                backgroundColor: item.color,
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                display: "inline-block",
                marginRight: "6px",
              }}
            />
            <Typography variant="body1">{item.name}</Typography>
          </div>
        ))}
      </Grid>
    </Container>
  );
};

export default Transitions;
